import { FC, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Box, IconButton, InputBase, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useSearchFilterNotesStore } from '@/stores/searchFilterNotesStore';

const COLLAPSED_WIDTH = 36;
const EXPANDED_WIDTH = 175;
// Wide enough that the longer "Search notes or people..." placeholder isn't clipped.
const EXPANDED_WIDTH_CUSTOM_PLACEHOLDER = 240;
const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 3;

interface ExpandableSearchProps {
  placeholder?: string;
}

const ExpandableSearch: FC<ExpandableSearchProps> = ({ placeholder }) => {
  const expandedWidth = placeholder ? EXPANDED_WIDTH_CUSTOM_PLACEHOLDER : EXPANDED_WIDTH;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const setSearchQuery = useSearchFilterNotesStore((s) => s.setSearchQuery);
  const initialQuery = useSearchFilterNotesStore((s) => s.searchQuery);
  const focusSearch = (location.state as { focusSearch?: boolean })?.focusSearch === true;
  const [isExpanded, setIsExpanded] = useState(() => initialQuery.length > 0 || focusSearch);
  const [inputValue, setInputValue] = useState(() => initialQuery);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasInteracted = useRef(false);

  // Clear the focusSearch state so it doesn't re-trigger on back navigation
  useEffect(() => {
    if (focusSearch) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [focusSearch, navigate, location.pathname]);

  // Debounce: update the store after the user stops typing
  useEffect(() => {
    if (!hasInteracted.current) return;

    const timeoutId = setTimeout(() => {
      if (inputValue.length >= MIN_SEARCH_LENGTH || inputValue.length === 0) {
        setSearchQuery(inputValue);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [inputValue, setSearchQuery]);

  const handleExpand = () => {
    setIsExpanded(true);
  };

  const handleCollapse = () => {
    hasInteracted.current = true;
    setIsExpanded(false);
    setInputValue('');
    setSearchQuery('');
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleCollapse();
    }
  };

  // Auto-focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      // Small delay to let the animation start before focusing
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  const mobileExpandedSx = {
    position: 'fixed' as const,
    top: 130,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 280,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    border: '1px solid',
    borderColor: 'primary.main',
    borderRadius: 1,
    backgroundColor: 'background.paper',
    zIndex: 1300,
    boxShadow: 3,
    overflow: 'hidden',
  };

  const defaultSx = {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    height: 36,
    width: isExpanded ? expandedWidth : COLLAPSED_WIDTH,
    minWidth: isExpanded ? expandedWidth : COLLAPSED_WIDTH,
    border: '1px solid',
    borderColor: isExpanded ? 'primary.main' : 'divider',
    borderRadius: 1,
    backgroundColor: isExpanded ? 'background.paper' : 'transparent',
    transition: 'width 0.3s ease, min-width 0.3s ease, border-color 0.3s ease, background-color 0.3s ease',
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: isExpanded ? 'background.paper' : 'action.hover',
    },
  };

  return (
    <Box sx={isMobile && isExpanded ? mobileExpandedSx : defaultSx}>
      {/* Search icon - always visible, acts as toggle button when collapsed */}
      <Tooltip title={isExpanded ? '' : 'Search notes'}>
        <IconButton
          size="small"
          aria-label={isExpanded ? 'Search' : 'Search notes'}
          onClick={isExpanded ? undefined : handleExpand}
          disableRipple={isExpanded}
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: 0.5,
            color: isExpanded ? 'primary.main' : 'action.active',
            cursor: isExpanded ? 'default' : 'pointer',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <SearchIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Expanding input area */}
      <InputBase
        inputRef={inputRef}
        value={inputValue}
        onChange={(e) => {
          hasInteracted.current = true;
          setInputValue(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder ?? 'Search notes...'}
        aria-label="Search notes"
        sx={{
          flex: 1,
          fontSize: '14px',
          fontWeight: 400,
          opacity: isExpanded ? 1 : 0,
          transition: 'opacity 0.2s ease 0.1s',
          '& .MuiInputBase-input': {
            padding: '4px 0',
            '&::placeholder': {
              color: 'text.secondary',
              opacity: 0.7,
            },
          },
        }}
      />

      {/* Close button - only visible when expanded */}
      <IconButton
        size="small"
        aria-label="Close search"
        onClick={handleCollapse}
        sx={{
          width: 28,
          height: 28,
          flexShrink: 0,
          mr: 0.25,
          opacity: isExpanded ? 1 : 0,
          transform: isExpanded ? 'scale(1)' : 'scale(0.5)',
          transition: 'opacity 0.2s ease 0.15s, transform 0.2s ease 0.15s',
          pointerEvents: isExpanded ? 'auto' : 'none',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      </IconButton>
    </Box>
  );
};

export default ExpandableSearch;
